// SeoResultDashboard.jsx - REDESIGNED VERSION
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from "jspdf-autotable";
import './SeoResultDashboard.css';
import Navbar from '../Common/Navbar';
import SecondaryFooter from '../Common/SecondaryFooter';

const SeoResultDashboard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // NEW STATE FOR REDESIGNED UI
  const [activeTab, setActiveTab] = useState('all');
  const [expandedIssues, setExpandedIssues] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [expandAll, setExpandAll] = useState(false);

  const API_BASE_URL = "http://localhost:8082/api/seo/audit";

  const getClientFriendlyError = (err) => {
    if (err.message === 'Failed to fetch' || err.message.includes('NetworkError')) {
      return {
        title: 'Connection Issue',
        message: 'We\'re having trouble connecting to our analysis service. Please check your internet connection and try again.',
        icon: '🌐'
      };
    }

    if (err.message.includes('timeout')) {
      return {
        title: 'Request Timeout',
        message: 'The analysis is taking longer than expected. The website might be slow to respond. Please try again.',
        icon: '⏱️'
      };
    }

    if (err.message.includes('status 404')) {
      return {
        title: 'Service Not Available',
        message: 'Our SEO analysis service is temporarily unavailable. Please try again in a few moments.',
        icon: '🔧'
      };
    }

    if (err.message.includes('status 500') || err.message.includes('status 503')) {
      return {
        title: 'Service Error',
        message: 'We encountered an error while analyzing your website. Our team has been notified. Please try again later.',
        icon: '⚠️'
      };
    }

    if (err.message.includes('status 429')) {
      return {
        title: 'Too Many Requests',
        message: 'You\'ve reached the analysis limit. Please wait a few minutes before trying again.',
        icon: '⏳'
      };
    }

    if (err.message.includes('invalid URL') || err.message.includes('malformed')) {
      return {
        title: 'Invalid Website URL',
        message: 'The website URL appears to be invalid. Please check the URL and try again.',
        icon: '🔗'
      };
    }

    if (err.message.includes('unreachable') || err.message.includes('not accessible')) {
      return {
        title: 'Website Unreachable',
        message: 'We couldn\'t access the website you provided. Please verify the URL is correct and the website is online.',
        icon: '🚫'
      };
    }

    if (err.message.includes('HTML instead of JSON') || err.message.includes('CORS')) {
      return {
        title: 'Analysis Unavailable',
        message: 'We\'re experiencing technical difficulties with our analysis service. Please try again shortly.',
        icon: '🔧'
      };
    }

    return {
      title: 'Analysis Failed',
      message: 'We encountered an unexpected error while analyzing your website. Please try again or contact support if the problem persists.',
      icon: '❌'
    };
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('userData');

    if (token && userData) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    const url = searchParams.get('url');

    if (!url) {
      setError({
        title: 'No Website Provided',
        message: 'Please enter a website URL to analyze.',
        icon: '🔍'
      });
      setLoading(false);
      return;
    }

    fetchSeoData(url);
  }, [searchParams]);

  // Check if user just logged in and should return here
  useEffect(() => {
    const redirectPath = localStorage.getItem('redirectAfterLogin');
    const currentPath = window.location.pathname + window.location.search;

    if (redirectPath === currentPath && isAuthenticated) {
      // User just logged in and returned to this page
      localStorage.removeItem('redirectAfterLogin');


    }
  }, [isAuthenticated]);

  const fetchSeoData = async (url) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}?url=${url}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`status ${response.status}`);
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const textResponse = await response.text();
        throw new Error('HTML instead of JSON');
      }

      const result = await response.json();

      setData(result);
      setLoading(false);

    } catch (err) {
      const friendlyError = getClientFriendlyError(err);
      setError(friendlyError);
      setLoading(false);
    }
  };

  const calculateScore = () => {
    if (!data || !data.issues || data.issues.length === 0) return 100;

    const high = data.issues.filter(i => i.severity === 'HIGH').length;
    const medium = data.issues.filter(i => i.severity === 'MEDIUM').length;
    const low = data.issues.filter(i => i.severity === 'LOW').length;

    const score = Math.max(0, 100 - (high * 20) - (medium * 10) - (low * 3));
    return Math.round(score);
  };

  const getGroupedIssues = () => {
    if (!data || !data.issues) return {};

    return data.issues.reduce((acc, issue) => {
      if (!acc[issue.severity]) acc[issue.severity] = [];
      acc[issue.severity].push(issue);
      return acc;
    }, {});
  };

  // NEW: Toggle individual issue expansion
  const toggleIssue = (issueId) => {
    setExpandedIssues(prev => ({
      ...prev,
      [issueId]: !prev[issueId]
    }));
  };

  // NEW: Expand/Collapse all issues
  const toggleExpandAll = () => {
    if (!data || !data.issues) return;

    const newExpandedState = {};
    if (!expandAll) {
      data.issues.forEach((issue, idx) => {
        newExpandedState[`${issue.severity}-${idx}`] = true;
      });
    }
    setExpandedIssues(newExpandedState);
    setExpandAll(!expandAll);
  };

  // NEW: Filter issues based on active tab and search
  const getFilteredIssues = () => {
    if (!data || !data.issues) return [];

    let filtered = [...data.issues];

    // Filter by tab
    if (activeTab !== 'all') {
      filtered = filtered.filter(issue => issue.severity === activeTab.toUpperCase());
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(issue =>
        issue.ruleCode.toLowerCase().includes(query) ||
        issue.message.toLowerCase().includes(query) ||
        (data.recommendations && data.recommendations[issue.ruleCode] &&
          data.recommendations[issue.ruleCode].toLowerCase().includes(query))
      );
    }

    return filtered;
  };

  const handleDownloadReport = () => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    // User is not logged in
    console.log('🔒 User not logged in - saving redirect path');
    
    // Save current page for redirect after login
    localStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
    
    // Redirect to login
    navigate('/login');
    return;
  }
  
  // User is logged in - proceed with download
  console.log('✅ User logged in - downloading report');
  // Your download logic here
};

  const generatePDFReport = () => {

    if (!isAuthenticated) {

      const currentPath = window.location.pathname + window.location.search;
      localStorage.setItem('redirectAfterLogin', currentPath);


      navigate('/login');
      return;
    }

    if (!data) return;


    const doc = new jsPDF();


    const score = data.score || calculateScore();
    const groupedIssues = getGroupedIssues();

    // Logo/Brand configuration
    const logoText = "ELEXYT";
    const brandColor = [126, 34, 206];

    // Colors
    const primaryColor = [13, 110, 253];
    const darkGray = [51, 51, 51];
    const mediumGray = [102, 102, 102];
    const lightGray = [245, 245, 245];
    const redColor = [220, 53, 69];
    const orangeColor = [255, 193, 7];
    const greenColor = [25, 135, 84];

    // Calculate score color
    const scoreColor =
      score >= 80 ? greenColor :
        score >= 60 ? primaryColor :
          score >= 40 ? orangeColor :
            redColor;

    let yPosition = 20;





    // Header gradient background
    doc.setFillColor(...brandColor);
    doc.rect(0, 0, 210, 45, "F");

    // Decorative accent line
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 44, 210, 1, "F");

    // Logo/Brand Box (left side)
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(15, 10, 60, 25, 3, 3, "F");
    doc.setTextColor(...brandColor);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(logoText, 45, 20, { align: "center" });
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("Professional SEO Analysis", 45, 28, { align: "center" });

    // Main title (right side)
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.text("SEO AUDIT REPORT", 140, 22, { align: "center" });
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Comprehensive Website Analysis", 140, 31, { align: "center" });

    yPosition = 55;

    doc.setTextColor(...darkGray);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Website URL:", 20, yPosition);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...primaryColor);
    doc.text(data.url || "N/A", 50, yPosition);

    yPosition += 7;
    doc.setTextColor(...mediumGray);
    doc.setFontSize(9);
    const generatedDate = data.generatedAt ? new Date(data.generatedAt).toLocaleString() : new Date().toLocaleString();
    doc.text(`Report Generated: ${generatedDate}`, 20, yPosition);
    yPosition += 10;


    // Outer shadow effect
    doc.setFillColor(200, 200, 200);
    doc.roundedRect(21, yPosition + 1, 170, 30, 3, 3, "F");

    // Main score box with border
    doc.setDrawColor(...scoreColor);
    doc.setLineWidth(2);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(20, yPosition, 170, 30, 3, 3, "FD");

    // Score circle with border
    doc.setFillColor(...scoreColor);
    doc.circle(40, yPosition + 15, 12, "F");
    doc.setFillColor(255, 255, 255);
    doc.circle(40, yPosition + 15, 10, "F");
    doc.setFillColor(...scoreColor);
    doc.circle(40, yPosition + 15, 8, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(score.toString(), 40, yPosition + 17, { align: "center" });

    // Score text
    doc.setTextColor(...darkGray);
    doc.setFontSize(16);
    doc.text("Overall SEO Score", 65, yPosition + 12);

    doc.setFontSize(11);
    const scoreStatus = score >= 80 ? "Excellent" : score >= 60 ? "Good" : score >= 40 ? "Needs Improvement" : "Poor";
    doc.setTextColor(...scoreColor);
    doc.text(`Status: ${scoreStatus}`, 65, yPosition + 21);

    yPosition += 40;

    const stats = [
      ["Load Time", `${data.loadTimeMs || 0}ms`, (data.loadTimeMs || 0) < 3000 ? "Good" : "Needs Improvement"],
      ["Security", data.usesHttps ? "HTTPS Enabled" : "HTTP Only", data.usesHttps ? "Secure" : "Warning"],
      ["HTTP Status", (data.httpStatus || "N/A").toString(), data.httpStatus === 200 ? "OK" : "Check Required"],
      ["Total Links", (data.totalLinks || 0).toString(), "Info"],
      ["Internal Links", (data.internalLinks || 0).toString(), "Info"],
      ["External Links", (data.externalLinks || 0).toString(), "Info"],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [["Metric", "Value", "Status"]],
      body: stats,
      theme: "grid",
      headStyles: {
        fillColor: brandColor,
        fontSize: 10,
        fontStyle: "bold",
        halign: "left",
        textColor: [255, 255, 255],
      },
      styles: {
        fontSize: 9,
        cellPadding: 5,
        lineColor: [230, 230, 230],
        lineWidth: 0.5,
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 50 },
        1: { cellWidth: 70 },
        2: { cellWidth: 50, halign: "center" },
      },
    });

    yPosition = doc.lastAutoTable.finalY + 15;

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...darkGray);
    doc.text("Meta Information", 20, yPosition);
    yPosition += 8;

    const metaData = [
      ["Page Title", data.title || "Not set", `${data.titleLength || 0} chars`],
      ["Meta Description", data.metaDescription || "Missing", `${data.metaDescriptionLength || 0} chars`],
      ["H1 Tags", (data.h1Tags ? data.h1Tags.length : 0).toString(), data.h1Tags?.length === 1 ? "Optimal" : "Check"],
      ["H2 Tags", (data.h2Tags ? data.h2Tags.length : 0).toString(), "Info"],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [["Element", "Content", "Status"]],
      body: metaData,
      theme: "striped",
      headStyles: { fillColor: primaryColor, fontSize: 10, fontStyle: "bold" },
      styles: { fontSize: 9, cellPadding: 4 },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 50 },
        1: { cellWidth: 90 },
        2: { cellWidth: 30, halign: "center" },
      },
    });

    yPosition = doc.lastAutoTable.finalY + 15;

    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...darkGray);
    doc.text("Technical Details", 20, yPosition);
    yPosition += 8;

    const technicalData = [
      ["Canonical Tag", data.hasCanonical ? "Yes" : "No", data.hasCanonical ? "✓" : "✗"],
      ["Structured Data", data.hasJsonLd ? "Yes" : "No", data.hasJsonLd ? "✓" : "✗"],
      ["Sitemap Found", data.sitemapFound ? "Yes" : "No", data.sitemapFound ? "✓" : "✗"],
      ["Robots.txt", data.robotsTxtAccessible ? "Accessible" : "Not Found", data.robotsTxtAccessible ? "✓" : "✗"],
      ["Images Missing Alt", (data.imagesMissingAlt || 0).toString(), data.imagesMissingAlt === 0 ? "✓" : "✗"],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [["Feature", "Status", "Check"]],
      body: technicalData,
      theme: "striped",
      headStyles: { fillColor: primaryColor, fontSize: 10, fontStyle: "bold" },
      styles: { fontSize: 9, cellPadding: 4 },
    });

    yPosition = doc.lastAutoTable.finalY + 15;

    if (data.issues && data.issues.length > 0) {
      if (yPosition > 230) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...darkGray);
      doc.text(`Issues Detected (${data.issues.length})`, 20, yPosition);
      yPosition += 8;

      if (groupedIssues.HIGH?.length) {
        doc.setFontSize(11);
        doc.setTextColor(...redColor);
        doc.text(`High Priority Issues (${groupedIssues.HIGH.length})`, 20, yPosition);
        yPosition += 6;

        const highIssuesData = groupedIssues.HIGH.map((issue) => [
          issue.ruleCode.replace(/_/g, " "),
          issue.message,
          "HIGH",
        ]);

        autoTable(doc, {
          startY: yPosition,
          head: [["Issue", "Description", "Severity"]],
          body: highIssuesData,
          theme: "grid",
          headStyles: { fillColor: redColor, fontSize: 9, fontStyle: "bold" },
          styles: { fontSize: 8, cellPadding: 3 },
        });

        yPosition = doc.lastAutoTable.finalY + 10;
      }

      if (groupedIssues.MEDIUM?.length) {
        if (yPosition > 240) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(11);
        doc.setTextColor(...orangeColor);
        doc.text(`Medium Priority Issues (${groupedIssues.MEDIUM.length})`, 20, yPosition);
        yPosition += 6;

        const mediumIssuesData = groupedIssues.MEDIUM.map((issue) => [
          issue.ruleCode.replace(/_/g, " "),
          issue.message,
          "MEDIUM",
        ]);

        autoTable(doc, {
          startY: yPosition,
          head: [["Issue", "Description", "Severity"]],
          body: mediumIssuesData,
          theme: "grid",
          headStyles: { fillColor: orangeColor, fontSize: 9, fontStyle: "bold" },
          styles: { fontSize: 8, cellPadding: 3 },
        });

        yPosition = doc.lastAutoTable.finalY + 10;
      }

      if (groupedIssues.LOW?.length) {
        if (yPosition > 240) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(11);
        doc.setTextColor(...primaryColor);
        doc.text(`Low Priority Issues (${groupedIssues.LOW.length})`, 20, yPosition);
        yPosition += 6;

        const lowIssuesData = groupedIssues.LOW.map((issue) => [
          issue.ruleCode.replace(/_/g, " "),
          issue.message,
          "LOW",
        ]);

        autoTable(doc, {
          startY: yPosition,
          head: [["Issue", "Description", "Severity"]],
          body: lowIssuesData,
          theme: "grid",
          headStyles: { fillColor: primaryColor, fontSize: 9, fontStyle: "bold" },
          styles: { fontSize: 8, cellPadding: 3 },
        });

        yPosition = doc.lastAutoTable.finalY + 10;
      }
    }

    if (data.recommendations && Object.keys(data.recommendations).length > 0) {
      if (yPosition > 200) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...darkGray);
      doc.text("Priority Recommendations", 20, yPosition);
      yPosition += 8;

      const recommendationsData = Object.entries(data.recommendations).map(([key, value]) => [
        key.replace(/_/g, " "),
        value,
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [["Issue", "Recommended Fix"]],
        body: recommendationsData,
        theme: "striped",
        headStyles: { fillColor: greenColor, fontSize: 10, fontStyle: "bold" },
        styles: { fontSize: 8, cellPadding: 4 },
        columnStyles: {
          0: { cellWidth: 60, fontStyle: "bold" },
          1: { cellWidth: 110 },
        },
      });
    }

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(...mediumGray);
      doc.text(
        `SEO Audit Report - ${data.url || "N/A"} - Page ${i} of ${pageCount}`,
        105,
        290,
        { align: "center" }
      );
    }

    // ============ ADD PAGE BORDERS ============


    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);

      // Page border
      doc.setDrawColor(...brandColor);
      doc.setLineWidth(1.5);
      doc.rect(10, 10, 190, 277, "S");

      // Footer text
      doc.setFontSize(8);
      doc.setTextColor(...mediumGray);
      doc.text(
        `SEO Audit Report - ${data.url || "N/A"}`,
        105,
        290,
        { align: "center" }
      );
      doc.text(
        `Page ${i} of ${pageCount}`,
        190,
        290,
        { align: "right" }
      );

      // Powered by text
      doc.setFontSize(7);
      doc.setTextColor(...brandColor);
      doc.text("Powered by ELEXYT PVT LTD", 20, 290);
    }

    doc.save(`seo-report-${new Date().getTime()}.pdf`);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading-container">
          <div className="loading-content">
            <div className="spinner-wrapper">
              <div className="spinner-outer"></div>
              <div className="spinner-inner"></div>
              <div className="spinner-center">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <path d="M20 5L25 15H15L20 5Z" fill="#0d6efd" />
                  <path d="M35 20L25 15L30 25L35 20Z" fill="#0d6efd" />
                  <path d="M20 35L15 25H25L20 35Z" fill="#0d6efd" />
                  <path d="M5 20L15 25L10 15L5 20Z" fill="#0d6efd" />
                </svg>
              </div>
            </div>
            <h3 className="loading-title">Analyzing Your Website</h3>
            <p className="loading-subtitle">Scanning SEO metrics and performance...</p>
            <div className="loading-steps">
              <div className="loading-step active">
                <div className="step-icon">🔍</div>
                <span>Crawling pages</span>
              </div>
              <div className="loading-step active delay-1">
                <div className="step-icon">📊</div>
                <span>Analyzing data</span>
              </div>
              <div className="loading-step active delay-2">
                <div className="step-icon">⚡</div>
                <span>Generating report</span>
              </div>
            </div>
          </div>
        </div>
        <SecondaryFooter />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
          flexDirection: 'column',
          gap: '20px',
          padding: '20px'
        }}>
          <div className="error-container" style={{
            maxWidth: '500px',
            textAlign: 'center',
            padding: '40px',
            background: '#fff',
            borderRadius: '16px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>
              {error.icon}
            </div>
            <h2 style={{
              color: '#333',
              marginBottom: '16px',
              fontSize: '24px',
              fontWeight: '600'
            }}>
              {error.title}
            </h2>
            <p style={{
              color: '#666',
              fontSize: '16px',
              lineHeight: '1.6',
              marginBottom: '30px'
            }}>
              {error.message}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                className="btn btn-primary"
                onClick={() => {
                  const url = searchParams.get('url');
                  if (url) fetchSeoData(url);
                }}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  borderRadius: '8px'
                }}
              >
                🔄 Try Again
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={() => navigate('/')}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  borderRadius: '8px'
                }}
              >
                ← Go Back
              </button>
            </div>
            <p style={{
              marginTop: '24px',
              fontSize: '14px',
              color: '#999'
            }}>
              Need help? <a href="/contact" style={{ color: '#0d6efd' }}>Contact Support</a>
            </p>
          </div>
        </div>
        <SecondaryFooter />
      </>
    );
  }

  if (!data) {
    return null;
  }

  const score = data.score || calculateScore();
  const groupedIssues = getGroupedIssues();
  const filteredIssues = getFilteredIssues();

  const getScoreClass = () => {
    if (score >= 80) return 'score-excellent';
    if (score >= 60) return 'score-good';
    if (score >= 40) return 'score-average';
    return 'score-poor';
  };

  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'HIGH': return 'severity-high';
      case 'MEDIUM': return 'severity-medium';
      case 'LOW': return 'severity-low';
      case 'INFO': return 'severity-info';
      default: return 'severity-info';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'HIGH': return '🔴';
      case 'MEDIUM': return '🟡';
      case 'LOW': return '🔵';
      default: return 'ℹ️';
    }
  };

  // Calculate summary stats for hero section
  const totalIssues = data.issues ? data.issues.length : 0;
  const criticalIssues = groupedIssues.HIGH ? groupedIssues.HIGH.length : 0;
  const warningIssues = groupedIssues.MEDIUM ? groupedIssues.MEDIUM.length : 0;
  const passedChecks = 24 - totalIssues; // Mock calculation, adjust based on your logic

  return (
    <>
      <Navbar />

      <div className="seo-dashboard-redesign">
        {/* HERO SUMMARY SECTION */}
        <div className="hero-summary">
          <div className="hero-header">
            <div className="hero-info">
              <h1 className="hero-title">SEO Audit Report</h1>
              <p className="hero-url">{data.url || 'N/A'}</p>
              <p className="hero-date">
                <span className="date-icon">📅</span>
                {data.generatedAt ? new Date(data.generatedAt).toLocaleString() : new Date().toLocaleString()}
              </p>
            </div>

            <div className="hero-score">
              <div className={`score-gauge ${getScoreClass()}`}>
                <svg className="score-circle-svg" viewBox="0 0 120 120">
                  <circle className="score-bg" cx="60" cy="60" r="54" />
                  <circle
                    className="score-progress"
                    cx="60"
                    cy="60"
                    r="54"
                    style={{
                      strokeDasharray: `${(score / 100) * 339.29}, 339.29`
                    }}
                  />
                </svg>
                <div className="score-content">
                  <span className="score-number">{score}</span>
                  <span className="score-label">Score</span>
                </div>
              </div>
            </div>
          </div>

          {/* QUICK STATS CARDS */}
          <div className="quick-stats">
            <div className="stat-card-new critical">
              <div className="stat-icon-new">🔴</div>
              <div className="stat-info">
                <span className="stat-number-new">{criticalIssues}</span>
                <span className="stat-label-new">Critical Issues</span>
              </div>
            </div>

            <div className="stat-card-new warning">
              <div className="stat-icon-new">🟡</div>
              <div className="stat-info">
                <span className="stat-number-new">{warningIssues}</span>
                <span className="stat-label-new">Warnings</span>
              </div>
            </div>

            <div className="stat-card-new total">
              <div className="stat-icon-new">📊</div>
              <div className="stat-info">
                <span className="stat-number-new">{totalIssues}</span>
                <span className="stat-label-new">Total Issues</span>
              </div>
            </div>

            <div className="stat-card-new passed">
              <div className="stat-icon-new">✅</div>
              <div className="stat-info">
                <span className="stat-number-new">{Math.max(0, passedChecks)}</span>
                <span className="stat-label-new">Passed Checks</span>
              </div>
            </div>
          </div>
        </div>

        
        {/* DOWNLOAD BUTTON */}
        <div className="download-section">
          <button
            className="btn-download"
            onClick={generatePDFReport}
            title={!isAuthenticated ? 'Click to login and download report' : 'Download PDF Report'}
          >
            <svg className="download-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Download PDF Report
          </button>
          

          {!isAuthenticated && (
            <div className="auth-message">
              <i className="fas fa-info-circle"></i>
              <span>You must be logged in to download reports</span>
            </div>
          )}
        </div>

        

        {/* MAIN CONTENT LAYOUT */}
        <div className="dashboard-content">
          {/* LEFT COLUMN - ISSUES */}
          <div className="dashboard-left">
            {/* ISSUES SECTION */}
            <div className="issues-section-new">
              <div className="issues-header">
                <h2 className="issues-title">
                  🔍 Issues Detected ({totalIssues})
                </h2>
                <div className="issues-controls">
                  <button
                    className="expand-all-btn"
                    onClick={toggleExpandAll}
                  >
                    {expandAll ? '📖 Collapse All' : '📂 Expand All'}
                  </button>
                </div>
              </div>

              {/* TAB NAVIGATION */}
              <div className="tab-navigation">
                <button
                  className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveTab('all')}
                >
                  All Issues ({totalIssues})
                </button>
                <button
                  className={`tab-button ${activeTab === 'high' ? 'active' : ''}`}
                  onClick={() => setActiveTab('high')}
                >
                  🔴 Critical ({criticalIssues})
                </button>
                <button
                  className={`tab-button ${activeTab === 'medium' ? 'active' : ''}`}
                  onClick={() => setActiveTab('medium')}
                >
                  🟡 Warnings ({warningIssues})
                </button>
                <button
                  className={`tab-button ${activeTab === 'low' ? 'active' : ''}`}
                  onClick={() => setActiveTab('low')}
                >
                  🔵 Low Priority ({groupedIssues.LOW ? groupedIssues.LOW.length : 0})
                </button>
              </div>

              {/* SEARCH BAR */}
              <div className="search-bar">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search issues..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* ISSUES LIST */}
              <div className="issues-list">
                {filteredIssues.length > 0 ? (
                  filteredIssues.map((issue, idx) => {
                    const issueId = `${issue.severity}-${idx}`;
                    const isExpanded = expandedIssues[issueId];

                    return (
                      <div
                        key={issueId}
                        className={`issue-accordion severity-${issue.severity} ${isExpanded ? 'expanded' : ''}`}
                      >
                        <div
                          className="issue-accordion-header"
                          onClick={() => toggleIssue(issueId)}
                        >
                          <div className="issue-accordion-left">
                            <div className="issue-icon-wrapper">
                              {getSeverityIcon(issue.severity)}
                            </div>
                            <div className="issue-title-wrapper">
                              <h3 className="issue-title-text">
                                {issue.ruleCode.replace(/_/g, ' ')}
                              </h3>
                              <p className="issue-category">{issue.severity} Priority</p>
                            </div>
                          </div>
                          <div className="issue-accordion-right">
                            <span className={`severity-badge-new ${issue.severity}`}>
                              {issue.severity}
                            </span>
                            <span className="expand-icon">
                              {isExpanded ? '▲' : '▼'}
                            </span>
                          </div>
                        </div>

                        <div className="issue-accordion-content">
                          <div className="issue-accordion-body">
                            <p className="issue-description">
                              {issue.message}
                            </p>
                            {data.recommendations && data.recommendations[issue.ruleCode] && (
                              <div className="issue-fix">
                                <span className="issue-fix-label">💡 Recommended Fix</span>
                                <p className="issue-fix-text">
                                  {data.recommendations[issue.ruleCode]}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-results">
                    <div className="no-results-icon">
                      {searchQuery ? '🔍' : '✅'}
                    </div>
                    <p className="no-results-text">
                      {searchQuery
                        ? `No issues found matching "${searchQuery}"`
                        : 'No issues found! Your website looks great.'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN - TECHNICAL DETAILS */}
          <div className="dashboard-right">
            {/* PERFORMANCE METRICS */}
            <div className="sidebar-card">
              <h3 className="sidebar-title">⚡ Performance Metrics</h3>
              <div className="metric-list">
                <div className="metric-item">
                  <span className="metric-label">Load Time</span>
                  <span className="metric-value">{data.loadTimeMs}ms</span>
                  <span className={`metric-status ${(data.loadTimeMs || 0) < 3000 ? 'good' : 'warning'}`}>
                    {(data.loadTimeMs || 0) < 3000 ? '✓ Good' : '⚠ Slow'}
                  </span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">HTTP Status</span>
                  <span className="metric-value">{data.httpStatus}</span>
                  <span className={`metric-status ${data.httpStatus === 200 ? 'good' : 'warning'}`}>
                    {data.httpStatus === 200 ? '✓ OK' : '⚠ Check'}
                  </span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Security</span>
                  <span className="metric-value">{data.usesHttps ? 'HTTPS' : 'HTTP'}</span>
                  <span className={`metric-status ${data.usesHttps ? 'good' : 'error'}`}>
                    {data.usesHttps ? '✓ Secure' : '✗ Not Secure'}
                  </span>
                </div>
              </div>
            </div>

            {/* META INFORMATION */}
            <div className="sidebar-card">
              <h3 className="sidebar-title">📄 Meta Information</h3>
              <div className="meta-list">
                <div className="meta-row">
                  <span className="meta-key">Page Title</span>
                  <span className="meta-chars">{data.titleLength || 0} chars</span>
                </div>
                <div className="meta-content">
                  {data.title || 'Not set'}
                </div>

                <div className="meta-row">
                  <span className="meta-key">Meta Description</span>
                  <span className="meta-chars">{data.metaDescriptionLength || 0} chars</span>
                </div>
                <div className="meta-content">
                  {data.metaDescription || 'Missing'}
                </div>

                <div className="meta-row">
                  <span className="meta-key">H1 Tags</span>
                  <span className={`meta-count ${data.h1Tags?.length === 1 ? 'optimal' : 'warning'}`}>
                    {data.h1Tags ? data.h1Tags.length : 0}
                    {data.h1Tags?.length === 1 ? ' ✓' : data.h1Tags?.length > 1 ? ' ⚠' : ''}
                  </span>
                </div>

                <div className="meta-row">
                  <span className="meta-key">H2 Tags</span>
                  <span className="meta-count">
                    {data.h2Tags ? data.h2Tags.length : 0}
                  </span>
                </div>
              </div>
            </div>

            {/* TECHNICAL CHECKS */}
            <div className="sidebar-card">
              <h3 className="sidebar-title">⚙️ Technical Checks</h3>
              <div className="check-list">
                <div className={`check-item ${data.hasCanonical ? 'passed' : 'failed'}`}>
                  <span className="check-icon">{data.hasCanonical ? '✓' : '✗'}</span>
                  <span className="check-label">Canonical Tag</span>
                </div>
                <div className={`check-item ${data.hasJsonLd ? 'passed' : 'failed'}`}>
                  <span className="check-icon">{data.hasJsonLd ? '✓' : '✗'}</span>
                  <span className="check-label">Structured Data</span>
                </div>
                <div className={`check-item ${data.sitemapFound ? 'passed' : 'failed'}`}>
                  <span className="check-icon">{data.sitemapFound ? '✓' : '✗'}</span>
                  <span className="check-label">Sitemap Found</span>
                </div>
                <div className={`check-item ${data.robotsTxtAccessible ? 'passed' : 'failed'}`}>
                  <span className="check-icon">{data.robotsTxtAccessible ? '✓' : '✗'}</span>
                  <span className="check-label">Robots.txt</span>
                </div>
                <div className={`check-item ${(data.imagesMissingAlt || 0) === 0 ? 'passed' : 'failed'}`}>
                  <span className="check-icon">{(data.imagesMissingAlt || 0) === 0 ? '✓' : '✗'}</span>
                  <span className="check-label">Images Alt Text</span>
                  <span className="check-count">
                    {data.imagesMissingAlt || 0} missing
                  </span>
                </div>
              </div>
            </div>

            {/* LINKS SUMMARY */}
            <div className="sidebar-card">
              <h3 className="sidebar-title">🔗 Links Summary</h3>
              <div className="links-grid">
                <div className="link-box total">
                  <span className="link-number">{data.totalLinks || 0}</span>
                  <span className="link-label">Total Links</span>
                </div>
                <div className="link-box internal">
                  <span className="link-number">{data.internalLinks || 0}</span>
                  <span className="link-label">Internal</span>
                </div>
                <div className="link-box external">
                  <span className="link-number">{data.externalLinks || 0}</span>
                  <span className="link-label">External</span>
                </div>
              </div>
            </div>

            {/* PRIORITY ACTIONS */}
            <div className="sidebar-card priority-card">
              <h3 className="sidebar-title">🎯 Priority Actions</h3>
              <div className="action-list-new">
                {!data.usesHttps && (
                  <div className="action-item">
                    <span className="action-icon">🔒</span>
                    <span className="action-text">Enable HTTPS with SSL certificate</span>
                  </div>
                )}
                {data.metaDescriptionLength === 0 && (
                  <div className="action-item">
                    <span className="action-icon">📝</span>
                    <span className="action-text">Add meta description</span>
                  </div>
                )}
                {!data.hasCanonical && (
                  <div className="action-item">
                    <span className="action-icon">🔗</span>
                    <span className="action-text">Add canonical tag</span>
                  </div>
                )}
                {data.imagesMissingAlt > 0 && (
                  <div className="action-item">
                    <span className="action-icon">🖼️</span>
                    <span className="action-text">Add alt text to {data.imagesMissingAlt} images</span>
                  </div>
                )}
                {data.h1Tags && data.h1Tags.length > 1 && (
                  <div className="action-item">
                    <span className="action-icon">📑</span>
                    <span className="action-text">Use single H1 tag per page</span>
                  </div>
                )}
                {!data.sitemapFound && (
                  <div className="action-item">
                    <span className="action-icon">🗺️</span>
                    <span className="action-text">Create and submit XML sitemap</span>
                  </div>
                )}
                {(!data.usesHttps && data.metaDescriptionLength === 0 && !data.hasCanonical &&
                  data.imagesMissingAlt === 0 && data.h1Tags?.length === 1 && data.sitemapFound) && (
                    <div className="no-actions">
                      <span className="no-actions-icon">✅</span>
                      <span className="no-actions-text">Great! No immediate actions needed.</span>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <SecondaryFooter />
    </>
  );
};

export default SeoResultDashboard;